/**
 * Sin interfaz: el INTERRUPTOR y la DESCARGA COMPLETA del modo sin conexión.
 *
 *   · Mira el interruptor de la empresa (`platform_settings.modo_sin_conexion`) al
 *     entrar y cada pocos minutos. Si se enciende, desde ya se guarda y se contesta
 *     desde el teléfono; si se apaga, se deja de hacer y se borra lo guardado.
 *   · Con el interruptor encendido, lanza la descarga completa
 *     (`sincronizacionOffline`) en segundo plano: un rato después de entrar (que la
 *     primera pantalla cargue antes), cada `CADA_MS` con la app a la vista y al
 *     volver la red. Cada pasada solo baja lo que toca.
 */
import { useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { alRecuperarRed, modoActivo } from "@/lib/offline/estadoOffline";
import { leerInterruptor } from "@/lib/offline/interruptorOffline";
import { apagarModoSinConexion, encenderModoSinConexion } from "@/lib/offline/sesionOffline";
import { sincronizar } from "@/lib/offline/sincronizacionOffline";

/** El interruptor se mira enseguida: de él depende cómo se comporta todo. */
const INTERRUPTOR_TRAS_MS = 2000;
/** La descarga, más tarde: primero lo que el usuario abre al entrar. */
const PRIMERA_DESCARGA_TRAS_MS = 15000;
const CADA_MS = 5 * 60 * 1000;

export function SincronizacionOffline() {
  const { user, company, role, loading } = useAuth();
  const empresa = company?.id ?? null;
  const ultimo = useRef<{ empresa: string | null; uid: string | null }>({ empresa: null, uid: null });

  useEffect(() => {
    if (loading || !user?.id || !empresa) return;
    const uid = user.id;
    // Al cambiar de empresa (perito) la copia es de la otra: se baja entera ya.
    const cambioDeEmpresa = ultimo.current.uid === uid && ultimo.current.empresa !== null && ultimo.current.empresa !== empresa;
    ultimo.current = { empresa, uid };
    let vivo = true;

    /** Mira el interruptor y aplica el cambio. Devuelve si queda encendido. */
    const revisarInterruptor = async (): Promise<boolean> => {
      const encendido = await leerInterruptor(empresa);
      if (!vivo || encendido === null) return modoActivo(); // sin respuesta: lo último que se supo
      if (encendido && !modoActivo()) encenderModoSinConexion(uid);
      if (!encendido && modoActivo()) await apagarModoSinConexion(uid);
      return encendido;
    };

    const descargar = (forzar = false) => {
      if (!vivo || document.visibilityState === "hidden" || !modoActivo()) return;
      void sincronizar({ empresa, rol: role, forzar }).catch(() => undefined);
    };

    const primeraRevision = window.setTimeout(() => void revisarInterruptor(), INTERRUPTOR_TRAS_MS);
    const primeraDescarga = window.setTimeout(
      () => void revisarInterruptor().then((on) => on && descargar(cambioDeEmpresa)),
      cambioDeEmpresa ? INTERRUPTOR_TRAS_MS : PRIMERA_DESCARGA_TRAS_MS,
    );
    const cada = window.setInterval(() => void revisarInterruptor().then((on) => on && descargar()), CADA_MS);
    const alVolver = () => {
      if (document.visibilityState === "visible") descargar();
    };
    document.addEventListener("visibilitychange", alVolver);
    const soltarRed = alRecuperarRed(() => descargar());
    return () => {
      vivo = false;
      window.clearTimeout(primeraRevision);
      window.clearTimeout(primeraDescarga);
      window.clearInterval(cada);
      document.removeEventListener("visibilitychange", alVolver);
      soltarRed();
    };
  }, [loading, user?.id, empresa, role]);

  return null;
}
